import React, { useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Text } from '@react-three/drei';
import * as THREE from 'three';

const RAIL_WIDTH = 4.8; // cm
const RAIL_DEPTH = 4.8; // cm
const PLAQUE_DEPTH = 1.3; // cm

// Composant pour afficher un élément de l'ossature (Rail ou Montant)
const OssatureElement = ({ element }) => {
    const isRail = element.type === 'rail';

    // Dimensions
    const width = isRail ? element.longueur : RAIL_WIDTH;
    const height = isRail ? RAIL_WIDTH : element.longueur;
    const depth = RAIL_DEPTH;

    // Position (Three.js utilise le centre de l'objet)
    const position = [
        element.x + width / 2,
        element.y + height / 2,
        0 // Centré sur Z=0
    ];

    // Couleur et Dimensions selon le type
    let color, lineColor;
    let w = width, h = height, d = depth;

    if (element.type === 'fourrure') {
        color = '#ffd43b'; // Jaune pour fourrures
        lineColor = '#f08c00';
        // Fourrure est plus plate et large : 4.5cm large, 1.8cm haut
        if (element.orientation === 'vertical') {
            w = 4.5; // Largeur visuelle
            d = 1.8; // Epaisseur
        } else {
            h = 4.5;
            d = 1.8;
        }
    } else if (element.type === 'corniere') {
        color = '#69db7c'; // Vert pour cornières
        lineColor = '#2b8a3e';
        d = 2; // Fine
    } else {
        // Standard Rail/Montant
        color = isRail ? '#ff6b6b' : '#4dabf7';
        lineColor = isRail ? '#c92a2a' : '#1864ab';
    }

    return (
        <mesh position={position}>
            <boxGeometry args={[w, h, d]} />
            <meshStandardMaterial color={color} transparent opacity={0.6} />
            <lineSegments>
                <edgesGeometry args={[new THREE.BoxGeometry(w, h, d)]} />
                <lineBasicMaterial color={lineColor} />
            </lineSegments>
        </mesh>
    );
};

// Composant pour afficher une plaque avec ses découpes éventuelles
const PlaqueElement = ({ plaque, index }) => {
    const shape = useMemo(() => {
        // Forme de base (rectangle complet)
        const s = new THREE.Shape();
        s.moveTo(0, 0);
        s.lineTo(plaque.largeur, 0);
        s.lineTo(plaque.largeur, plaque.hauteur);
        s.lineTo(0, plaque.hauteur);
        s.lineTo(0, 0);

        // Ajouter les découpes (trous)
        if (plaque.decoupes && plaque.decoupes.length > 0) {
            plaque.decoupes.forEach(d => {
                // Calcul des coordonnées locales de la découpe
                // Note: d.xLocal/yLocal sont relatifs au coin bas-gauche de la plaque
                const x = d.xLocal || (d.x - plaque.x);
                const y = d.yLocal || (d.y - plaque.y);

                const hole = new THREE.Path();
                hole.moveTo(x, y);
                hole.lineTo(x + d.largeur, y);
                hole.lineTo(x + d.largeur, y + d.hauteur);
                hole.lineTo(x, y + d.hauteur);
                hole.lineTo(x, y);
                s.holes.push(hole);
            });
        }

        return s;
    }, [plaque]);

    // Position de la plaque
    // On décale légèrement en Z pour qu'elle soit devant l'ossature
    // Ossature est centrée en Z=0 et fait 4.8 de large (-2.4 à +2.4)
    // Plaque commence à Z=2.4
    const position = [plaque.x, plaque.y, RAIL_DEPTH / 2];

    return (
        <group position={position}>
            <mesh>
                <extrudeGeometry args={[shape, { depth: PLAQUE_DEPTH, bevelEnabled: false }]} />
                <meshStandardMaterial color="#e9f0ff" side={THREE.DoubleSide} />
                <lineSegments>
                    <edgesGeometry args={[new THREE.ExtrudeGeometry(shape, { depth: PLAQUE_DEPTH, bevelEnabled: false })]} />
                    <lineBasicMaterial color="#a5d8ff" />
                </lineSegments>
            </mesh>
            {/* Numéro de la plaque */}
            <Text
                position={[plaque.largeur / 2, plaque.hauteur / 2, PLAQUE_DEPTH + 0.1]}
                fontSize={10}
                color="#3a86ff"
                anchorX="center"
                anchorY="middle"
            >
                #{index + 1}
            </Text>
        </group>
    );
};

// Composant groupant tout le contenu d'un mur (Ossature + Plaques)
export const Mur3DGroup = ({ mur, ossature, plaques, isSelected, onClick }) => {
    // Calcul du centre pour positionnement éventuel (non utilisé ici si on respecte les coords du mur)
    // Mais Utile pour le Highlight

    // Gestion de la rotation (soit un nombre pour Y, soit un tableau [x,y,z])
    const rotation = Array.isArray(mur.rotation)
        ? mur.rotation
        : [0, mur.rotation || 0, 0];

    return (
        <group
            position={[mur.position?.x || 0, mur.position?.y || 0, mur.position?.z || 0]}
            rotation={rotation}
            onClick={(e) => {
                if (onClick) {
                    e.stopPropagation();
                    onClick(mur.id);
                }
            }}
        >
            {/* Surbrillance si sélectionné */}
            {isSelected && (
                <mesh position={[mur.largeur / 2, mur.hauteur / 2, -1]}>
                    <planeGeometry args={[mur.largeur + 10, mur.hauteur + 10]} />
                    <meshBasicMaterial color="#ffff00" opacity={0.3} transparent side={THREE.DoubleSide} />
                </mesh>
            )}

            {/* Ossature */}
            <group>
                {ossature && ossature.elements && ossature.elements.map((el, idx) => (
                    <OssatureElement key={`ossature-${idx}`} element={el} />
                ))}
            </group>

            {/* Plaques */}
            <group>
                {plaques && plaques.map((plaque, idx) => (
                    <PlaqueElement key={`plaque-${idx}`} plaque={plaque} index={idx} />
                ))}
            </group>

            {/* Label du mur (visible de loin) */}
            <Text
                position={[mur.largeur / 2, mur.hauteur + 20, 0]}
                fontSize={20}
                color="#333"
                anchorX="center"
                anchorY="bottom"
            >
                {mur.nom}
            </Text>
        </group>
    );
};

export default function MurVisualisation3D({ mur, ossature, resultat }) {
    // Récupérer les plaques spécifiques à ce mur depuis le résultat global
    const plaques = useMemo(() => {
        if (!resultat) return [];
        return resultat.filter(p => p.murId === mur.id);
    }, [resultat, mur.id]);

    // Si le mur n'a pas de position/rotation définies, on utilise 0 (mode vue unique)
    const murAvecDefaut = {
        ...mur,
        position: { x: 0, y: 0, z: 0 },
        rotation: 0
    };

    // Calcul du centre pour la caméra par défaut
    const centerX = mur.largeur / 2;
    const centerY = mur.hauteur / 2;
    const distance = Math.max(mur.largeur, mur.hauteur);

    return (
        <div style={{ width: '100%', height: '500px', backgroundColor: '#f0f2f5', borderRadius: '8px', overflow: 'hidden' }}>
            <Canvas
                camera={{ position: [centerX, centerY, distance], fov: 50 }}
                shadows
            >
                <color attach="background" args={['#f0f2f5']} />

                {/* Lumières */}
                <ambientLight intensity={0.7} />
                <pointLight position={[centerX, centerY, distance]} intensity={0.8} />
                <spotLight position={[-100, centerY * 2, 200]} angle={0.4} penumbra={1} intensity={0.5} castShadow />

                {/* Contrôles */}
                <OrbitControls target={[centerX, centerY, 0]} />

                {/* Grille de sol pour repère */}
                <gridHelper args={[1000, 20]} rotation={[Math.PI / 2, 0, 0]} position={[centerX, -5, 0]} />

                {/* Contenu du Mur */}
                <Mur3DGroup mur={murAvecDefaut} ossature={ossature} plaques={plaques} />

                <mesh position={[centerX, -6, 0]} rotation={[-Math.PI / 2, 0, 0]}>
                    <planeGeometry args={[1000, 1000]} />
                    <meshStandardMaterial color="#dee2e6" />
                </mesh>

            </Canvas>
        </div>
    );
}
